import { immerable } from 'immer'
import { sum } from 'radash'

import {
	BookableNotPackagedFeature,
	CheckInFeature,
	PackagePriceDto,
	TaxDto
} from '@wwt/entities/api/search-response'
import { OfferWithoutPrice } from '@wwt/entities/search-response'

import { roundTo2 } from '@/shared/lib/roundTo2'

import { PriceFeature } from '../types/PriceFeature'
import { OfferPriceUtils } from '../utils/OfferPriceUtils'

type PriceTax = Omit<TaxDto, 'isIncluded'>

interface OfferPriceFacade<T> {
	/**
	 * Initialize offer price
	 *
	 * This method should be called before any usage of object
	 */
	init: (offer: OfferWithoutPrice) => T

	/**
	 * Initialize offer price based on separate price info
	 *
	 * This method should be called before any usage of object instead of
	 * {@link init} if needed
	 */
	initSeparate: (
		packagePrice: PackagePriceDto,
		checkIn: CheckInFeature[],
		notPackaged: BookableNotPackagedFeature[]
	) => T

	/**
	 * Price user will pay before check-in on our platform
	 *
	 * Includes price of package and not packaged features and **discount**
	 *
	 * @param {boolean} rounded - Return rounded price if enabled
	 */
	bookingPrice: (rounded?: boolean) => number

	/**
	 * Price user will pay on check-in
	 *
	 * Includes price of all check-in features and not included taxes
	 *
	 * @param {boolean} rounded - Return rounded price if enabled
	 */
	checkInPrice: (rounded?: boolean) => number

	/**
	 * Total price of offer
	 *
	 * @param {boolean} rounded - Return rounded price if enabled
	 * @param {boolean} includeDiscount - Return price including discount if enabled
	 */
	totalPrice: (rounded?: boolean, includeDiscount?: boolean) => number

	/**
	 * Price of offer per day
	 *
	 * Return {@link totalPrice} divided by {@link dayCount}
	 *
	 * @param {boolean} rounded - Return rounded price if enabled
	 * @param {boolean} includeDiscount - Return price including discount if enabled
	 */
	pricePerDay: (
		dayCount: number,
		rounded?: boolean,
		includeDiscount?: boolean
	) => number

	/**
	 * Price of all additional services(aka not packaged)
	 *
	 * Price included sum of all not packaged
	 *
	 * Unlike {@link bookingPrice} this price doesn't include package and discount
	 *
	 * @param {boolean} rounded - Return rounded price if enabled
	 */
	additionalServicesPrice: (rounded?: boolean) => number

	/**
	 * Price of all pay-at-check-in services(aka check-in)
	 *
	 * Price included sum of all check-in
	 *
	 * Unlike {@link checkInPrice} this price doesn't include taxes
	 *
	 * @param {boolean} rounded - Return rounded price if enabled
	 */
	payAtCheckInServicesPrice: (rounded?: boolean) => number

	/**
	 * Discount as price
	 *
	 * @param {boolean} rounded - Return rounded price if enabled
	 */
	discountPrice: (rounded?: boolean) => number

	/**
	 * Discount as percent from {@link bookingPrice} without discount
	 *
	 * @param {boolean} rounded - Return rounded price if enabled
	 */
	discountPercent: (rounded?: boolean) => number

	/**
	 * Price of all included taxes
	 *
	 * @param {boolean} rounded - Return rounded price if enabled
	 */
	includedTaxPrice: (rounded?: boolean) => number

	/**
	 * Price of all not included taxes
	 *
	 * @param {boolean} rounded - Return rounded price if enabled
	 */
	notIncludedTaxPrice: (rounded?: boolean) => number

	/**
	 * Get detailed price of offer
	 *
	 * This method return only inner info without prices we can get by other methods
	 *
	 * @param {boolean} rounded - Return rounded price if enabled
	 * @param {boolean} filterZero - Remove additionalServices and payAtCheckInServices with zero price
	 */
	detailed: (
		rounded?: boolean,
		filterZero?: boolean
	) => {
		/**
		 * Price of package
		 */
		package: number

		/**
		 * List of all included taxes in package price
		 */
		includedTax: PriceTax[]

		/**
		 * List of all NOT included taxes in package price
		 */
		notIncludedTax: PriceTax[]

		/**
		 * List all all additional services
		 */
		additionalServices: PriceFeature[]

		/**
		 * List of all pay-at-check-in services
		 */
		payAtCheckInServices: PriceFeature[]
	}
}

/**
 * Private fields of OfferPrice
 *
 * @private
 */
interface OfferPricePrivate {
	[immerable]: boolean

	_package: number
	_includedTax: PriceTax[]
	_notIncludedTax: PriceTax[]
	_additionalServices: PriceFeature[]
	_payAtCheckInServices: PriceFeature[]
	_discount: number

	_roundToNumber: (value: number) => number
	_roundToFloat: (value: number) => number

	_bookingPriceWithoutDiscount: () => number
	_payAtCheckInServicesPrice: () => number
	_additionalServicesPrice: () => number
	_includedTaxPrice: () => number
	_notIncludedTaxPrice: () => number
	_totalPriceWithoutRound: (includeDiscount?: boolean) => number
	_sumAmount: <T extends { amount: number }>(elements: T[]) => number
	_roundAmounts: <T extends { amount: number }>(
		elements: T[],
		rounded: boolean
	) => T[]

	_filterZero: <T extends { amount: number }>(elements: T[]) => T[]
	_roundIfNeeded: (price: number, rounded: boolean) => number
}

export type OfferPrice = OfferPriceFacade<OfferPrice> & OfferPricePrivate

// TODO: Support currency
export const OfferPrice: OfferPrice = {
	[immerable]: true,

	_package: -1,
	_includedTax: [],
	_notIncludedTax: [],

	_additionalServices: [],
	_payAtCheckInServices: [],

	_discount: 0,

	_roundToNumber: Math.ceil,
	_roundToFloat: roundTo2,

	init(offer: OfferWithoutPrice) {
		this[immerable] = true

		const selectedPackage = OfferPriceUtils.selectedPackagePrice(offer)

		this._package = selectedPackage.amount
		this._includedTax = OfferPriceUtils.taxesByIncluded(
			selectedPackage.taxes,
			true
		)
		this._notIncludedTax = OfferPriceUtils.taxesByIncluded(
			selectedPackage.taxes,
			false
		)

		this._additionalServices = OfferPriceUtils.notPackagedPrices(offer)
		this._payAtCheckInServices = OfferPriceUtils.checkInFeaturePrices(offer)

		this._discount = selectedPackage.wwtPromoCodeDiscount ?? 0

		return this
	},

	initSeparate(packagePrice, checkIn, notPackaged) {
		this._package = packagePrice.amount
		this._includedTax = OfferPriceUtils.taxesByIncluded(
			packagePrice.taxes,
			true
		)
		this._notIncludedTax = OfferPriceUtils.taxesByIncluded(
			packagePrice.taxes,
			false
		)

		this._additionalServices = OfferPriceUtils.sortByAmount(notPackaged)
		this._payAtCheckInServices = OfferPriceUtils.sortByAmount(checkIn)

		this._discount = packagePrice.wwtPromoCodeDiscount ?? 0

		return this
	},

	bookingPrice(rounded = false) {
		const price = this._bookingPriceWithoutDiscount() - this._discount

		return this._roundIfNeeded(price, rounded)
	},

	checkInPrice(rounded = false) {
		const price =
			this._payAtCheckInServicesPrice() + this._notIncludedTaxPrice()

		return this._roundIfNeeded(price, rounded)
	},

	totalPrice(rounded = false, includeDiscount = true) {
		const price = this._totalPriceWithoutRound(includeDiscount)

		return this._roundIfNeeded(price, rounded)
	},

	pricePerDay(dayCount, rounded = false, includeDiscount = true) {
		const price = this._totalPriceWithoutRound(includeDiscount) / dayCount

		return this._roundIfNeeded(price, rounded)
	},

	additionalServicesPrice(rounded = false) {
		const price = this._additionalServicesPrice()

		return this._roundIfNeeded(price, rounded)
	},

	payAtCheckInServicesPrice(rounded = false) {
		const price = this._payAtCheckInServicesPrice()

		return this._roundIfNeeded(price, rounded)
	},

	discountPrice(rounded = false) {
		const price = this._discount

		return this._roundIfNeeded(price, rounded)
	},

	discountPercent(rounded = false) {
		const price = (this._discount * 100) / this._bookingPriceWithoutDiscount()

		return this._roundIfNeeded(price, rounded)
	},

	notIncludedTaxPrice(rounded = false) {
		const price = this._notIncludedTaxPrice()

		return this._roundIfNeeded(price, rounded)
	},

	includedTaxPrice(rounded = false) {
		const price = this._includedTaxPrice()

		return this._roundIfNeeded(price, rounded)
	},

	detailed(rounded = false, filterZero = false) {
		const additionalServices = this._roundAmounts(
			this._additionalServices,
			rounded
		)

		const payAtCheckInServices = this._roundAmounts(
			this._payAtCheckInServices,
			rounded
		)

		return {
			package: this._roundIfNeeded(this._package, rounded),
			includedTax: this._roundAmounts(this._includedTax, rounded),
			notIncludedTax: this._roundAmounts(this._notIncludedTax, rounded),
			additionalServices: filterZero
				? this._filterZero(additionalServices)
				: additionalServices,
			payAtCheckInServices: filterZero
				? this._filterZero(payAtCheckInServices)
				: payAtCheckInServices
		}
	},

	_roundIfNeeded(price, rounded) {
		return rounded ? this._roundToNumber(price) : this._roundToFloat(price)
	},

	_bookingPriceWithoutDiscount() {
		return this._package + this._additionalServicesPrice()
	},

	_payAtCheckInServicesPrice() {
		return this._sumAmount(this._payAtCheckInServices)
	},

	_additionalServicesPrice() {
		return this._sumAmount(this._additionalServices)
	},

	_includedTaxPrice() {
		return this._sumAmount(this._includedTax)
	},

	_notIncludedTaxPrice() {
		return this._sumAmount(this._notIncludedTax)
	},

	_totalPriceWithoutRound(includeDiscount = true) {
		return (
			this._package +
			this._additionalServicesPrice() +
			this._payAtCheckInServicesPrice() +
			this._notIncludedTaxPrice() +
			(includeDiscount ? -this._discount : 0)
		)
	},

	// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
	_sumAmount<T extends { amount: number }>(elements: T[]) {
		return sum(elements, el => el.amount)
	},

	_roundAmounts<T extends { amount: number }>(elements: T[], rounded: boolean) {
		return elements.map(el => ({
			...el,
			amount: this._roundIfNeeded(el.amount, rounded)
		}))
	},

	// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
	_filterZero<T extends { amount: number }>(elements: T[]) {
		return elements.filter(el => el.amount > 0)
	}
}
